/** Twelve hours: a working day's worth of freshness. */
export const STALE_AFTER_MS = 12 * 60 * 60 * 1000;

const STORAGE_KEY = "offline_prefetch_status";

export type PrefetchState = "never" | "running" | "ready" | "partial" | "failed";

export interface PrefetchStatus {
  state: PrefetchState;
  /** Set only on a fully successful run — a partial run must not look fresh. */
  lastCompletedAt: number | null;
  routesTotal: number;
  routesDone: number;
  routesFailed: number;
  contactsDone: boolean;
  /**
   * Households on the volunteer's routes whose visit-report data could not be
   * downloaded. Non-zero means a report cannot be filed for those stops offline,
   * so the run is never "ready" — the same rule a failed route follows.
   */
  reportsFailed: number;
  /** True when a restricted storage quota forced us to skip the contact book. */
  storageLimited: boolean;
}

export const DEFAULT_STATUS: PrefetchStatus = {
  state: "never",
  lastCompletedAt: null,
  routesTotal: 0,
  routesDone: 0,
  routesFailed: 0,
  contactsDone: false,
  reportsFailed: 0,
  storageLimited: false,
};

export const readPrefetchStatus = (): PrefetchStatus => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATUS };
    return { ...DEFAULT_STATUS, ...JSON.parse(raw) };
  } catch {
    // Corrupt value or localStorage unavailable: behave as never-downloaded, which
    // is the honest answer and triggers a fresh attempt.
    return { ...DEFAULT_STATUS };
  }
};

export const writePrefetchStatus = (status: PrefetchStatus): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(status));
  } catch {
    // Storage full or blocked. The run itself still happened; only the record of
    // it is lost, which costs us one redundant refresh later.
  }
};

export const clearPrefetchStatus = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to do */
  }
};

/**
 * Anything short of a complete, recent success is stale. A `partial` result is
 * always stale regardless of age: the volunteer is not covered, and making them
 * wait out the 12-hour window would leave them uncovered all day.
 */
export const isStale = (status: PrefetchStatus, now: number): boolean => {
  if (status.state !== "ready") return true;
  if (status.lastCompletedAt === null) return true;
  return now - status.lastCompletedAt > STALE_AFTER_MS;
};
